import { ProfilePhotoProvider } from "./providers/ProfilePhotoProvider";
import React, { Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Navigate, Route, Routes } from "react-router-dom";

import "./App.css";
import Layout from "./layout/Layout";
import SiteFooter from "./layout/SiteFooter";
import { LocalizationProvider, LocalizationRouteBoundary } from "./localization";
import { AccountReadinessProvider } from "./providers/AccountReadinessProvider";
import BrowserPushLifecycle from "./push/BrowserPushLifecycle";
import NotificationLocaleLifecycle from "./push/NotificationLocaleLifecycle";
import AccountNotices from "./pages/auth/AccountNotices";
import { AppBootstrapProvider } from "./providers/AppBootstrapProvider";
import { PublicRaceLaunchProvider } from "./providers/PublicRaceLaunchProvider";
import { UnitsProvider } from "./providers/UnitsProvider";
import ProtectedRoute from "./pages/auth/Protected";
import RouteAccessibility from "./accessibility/RouteAccessibility";
import PublicPageHead from "./localization/PublicPageHead";
import PrivacyConsentManager from "./privacy/PrivacyConsentManager";
import { PrivacyConsentProvider } from "./privacy/PrivacyConsentProvider";
import ConversionTracker from "./analytics/ConversionTracker";
import PageLoadingIndicator from "./components/PageLoadingIndicator";
import TemporaryLaunchGate from "./pages/landing/TemporaryLaunchGate";

import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/selectChevron.css";
import "./styles/editorial-system.css";

const RacesList = React.lazy(() => import("./pages/races/RacesList"));
const Race = React.lazy(() => import("./pages/races/Race"));
const PublicRacePage = React.lazy(() => import("./pages/races/PublicRacePage"));
const RaceCreatePage = React.lazy(() => import("./pages/races/RaceCreatePage"));
const RaceResultsPage = React.lazy(() => import("./pages/races/RaceResultsPage"));
const RaceResultMapPage = React.lazy(() => import("./pages/races/RaceResultMapPage"));
const RegisteredRacersPage = React.lazy(
  () => import("./pages/races/RegisteredRacersPage")
);
const FinishSignup = React.lazy(() => import("./pages/auth/FinishSignup"));
const Signup = React.lazy(() => import("./pages/auth/Signup"));
const Login = React.lazy(() => import("./pages/auth/Login"));
const ForgotPassword = React.lazy(() => import("./pages/auth/ForgotPassword"));
const LandingPage = React.lazy(() => import("./pages/landing/LandingPage"));
const DiscoverPage = React.lazy(() => import("./pages/discover/DiscoverPage"));
const DiscoverFullMapPage = React.lazy(
  () => import("./pages/discover/DiscoverFullMapPage")
);
const RegisterPage = React.lazy(() => import("./pages/register/RegisterPage"));
const LiveRacersPage = React.lazy(() => import("./pages/live-racers/LiveRacersPage"));
const OrganizerLaunchPage = React.lazy(
  () => import("./pages/organizers/OrganizerLaunchPage")
);
const AdminPage = React.lazy(() => import("./pages/admin/AdminPage"));
const AdminRaceSubmissionReviewPage = React.lazy(
  () => import("./pages/admin/AdminRaceSubmissionReviewPage")
);
const TermsPage = React.lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/legal/PrivacyPolicyPage"));
const CookiePolicyPage = React.lazy(() => import("./pages/legal/CookiePolicyPage"));
const AccessibilityPage = React.lazy(() => import("./pages/legal/AccessibilityPage"));
const SupportPage = React.lazy(() => import("./pages/support/SupportPage"));
const SenyIRauxaPage = React.lazy(() => import("./pages/brand/SenyIRauxaPage"));
const CareersPage = React.lazy(() => import("./pages/careers/CareersPage"));
const AccountPage = React.lazy(() => import("./pages/account/AccountPage"));
const ProfilePage = React.lazy(() => import("./pages/profile/ProfilePage"));
const RaceParticipationPage = React.lazy(() => import("./pages/races/RaceParticipationPage"));
const SettingsPage = React.lazy(() => import("./pages/settings/SettingsPage"));

const routeFallback = <PageLoadingIndicator className="app-route-loading" />;

const handleSkipToMain = (event: React.MouseEvent<HTMLAnchorElement>) => {
  const mainContent = document.getElementById("main-content");
  if (!mainContent) {
    return;
  }

  event.preventDefault();
  mainContent.focus({ preventScroll: true });
  mainContent.scrollIntoView({ block: "start" });
};

function App() {
  return (
    <>
      <LocalizationProvider>
        <PrivacyConsentProvider>
          <AppBootstrapProvider>
            <AccountReadinessProvider>
            <ProfilePhotoProvider>
            <BrowserPushLifecycle />
            <NotificationLocaleLifecycle />
            <PublicRaceLaunchProvider>
              <UnitsProvider>
              <Router
                future={{
                  v7_relativeSplatPath: true,
                  v7_startTransition: true,
                }}
              >
              <LocalizationRouteBoundary>
              <TemporaryLaunchGate>
                <div className="app-shell">
                <a className="skip-link" href="#main-content" onClick={handleSkipToMain}>
                  Skip to main content
                </a>
                <RouteAccessibility />
                <PublicPageHead />
                <ConversionTracker />
                <PrivacyConsentManager />
                <AccountNotices />
                <div className="app-shell-main">
                  <Suspense fallback={routeFallback}>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/home" element={<LandingPage />} />
                      <Route path="/landing" element={<Navigate to="/" replace />} />
                      <Route path="/finish-signup" element={<FinishSignup />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route
                        path="/legal/terms"
                        element={
                          <Layout useAuthTopNav>
                            <TermsPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/legal/privacy"
                        element={
                          <Layout useAuthTopNav>
                            <PrivacyPolicyPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/legal/cookies"
                        element={
                          <Layout useAuthTopNav>
                            <CookiePolicyPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/accessibility"
                        element={
                          <Layout useAuthTopNav>
                            <AccessibilityPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/support"
                        element={
                          <Layout useAuthTopNav>
                            <SupportPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/seny-i-rauxa"
                        element={
                          <Layout useAuthTopNav>
                            <SenyIRauxaPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/careers"
                        element={
                          <Layout useAuthTopNav>
                            <CareersPage />
                          </Layout>
                        }
                      />
                      <Route path="/contact" element={<Navigate to="/support" replace />} />
                      <Route
                        path="/contact-corsa"
                        element={<Navigate to="/support" replace />}
                      />
                      <Route
                        path="/discover"
                        element={
                          <Layout useAuthTopNav>
                            <DiscoverPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/discover/map"
                        element={
                          <Layout useAuthTopNav>
                            <DiscoverFullMapPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/race/:raceId"
                        element={
                          <Layout useAuthTopNav>
                            <PublicRacePage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RegisterPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/live-racers"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <LiveRacersPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/organize"
                        element={
                          <Layout useAuthTopNav>
                            <OrganizerLaunchPage />
                          </Layout>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Layout useAuthTopNav>
                              <AdminPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/race-submissions/:id"
                        element={
                          <ProtectedRoute requireAdmin>
                            <Layout useAuthTopNav>
                              <AdminRaceSubmissionReviewPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RacesList />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races/new"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RaceCreatePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races/:id/registered-users"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RegisteredRacersPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races/:id/results"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RaceResultsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races/:id/results/map"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RaceResultMapPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/races/:id"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <Race />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/account"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <AccountPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <ProfilePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/:userId/races/:userRaceId"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <RaceParticipationPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/:userId"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <ProfilePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Layout useAuthTopNav>
                              <SettingsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </div>
                <SiteFooter />
                </div>
              </TemporaryLaunchGate>
              </LocalizationRouteBoundary>
              </Router>
              </UnitsProvider>
            </PublicRaceLaunchProvider>
            </ProfilePhotoProvider>
            </AccountReadinessProvider>
          </AppBootstrapProvider>
        </PrivacyConsentProvider>
      </LocalizationProvider>
    </>
  );
}

export default App;
